import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { useToast } from 'Store/toast';
import { useLoader } from 'Store/loader';
import { useUserInfo } from 'Store/userInfo';
import patchRoleAccess from 'Api/role/patchRoleAccess';

import { useRoleState } from 'View/role/state';

import upsertRoleAccessPayload from './model/request/upsertRoleAccessPayload';

const usePatchRoleAccess = (setError, clearErrors) => {
  const { t } = useTranslation('role');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { updateState, state, filterState } = useRoleState();
  const { organization } = useUserInfo();

  const { onOpenToast } = useToast();
  const { onShowLoader, onResetLoader } = useLoader();

  return useMutation({
    mutationFn: async (formData) => {
      const payload = upsertRoleAccessPayload(formData, state?.hostMenu);

      const id = formData?.id;

      await patchRoleAccess({ id, payload });
    },
    onMutate: () => {
      onShowLoader({ variant: 'loader-primary' });
    },
    onSuccess: () => {
      clearErrors();

      navigate('/master-data/access');
      onOpenToast({
        status: 'success',
        description: t('updateSuccess'),
        duration: 5,
        alwaysShow: false,
      });

      queryClient.invalidateQueries({ queryKey: ['role', filterState] });
      queryClient.invalidateQueries({ queryKey: ['all-roles', organization?.id] });
    },
    onError: (error) => {
      const { response } = error;
      const errorFields = response?.data?.errorFields;

      clearErrors();

      if (!isEmpty(errorFields)) {
        setError('name', { type: 'custom', message: errorFields?.name?.message || '' });
        return;
      }

      updateState({
        isOpenFormUpsert: false,
        alertProps: {
          isOpen: true,
          type: 'error',
          title: t('updateErrorTitle'),
          description: t('updateErrorDescription'),
          onClose: () => updateState({ alertProps: {} }),
          marginBottom: '12px',
        },
      });

      queryClient.invalidateQueries({ queryKey: ['role', filterState] });

      navigate('/master-data/access');
    },
    onSettled: () => {
      onResetLoader();
    },
  });
};

export default usePatchRoleAccess;